import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "@/reducers/hooks"
import { resolveDriverDisplayName } from "@/components/cmrReport/resolveDriverDisplayName"
import { formatJidLocalpartAsName } from "@/components/cmrReport/resolveSearchDriverName"

/**
 * Resolve a driver's human display name for the fuel views: the configured
 * chat/profile name first (same source the fuel report table uses), falling
 * back to the profile's first/last name, then a title-cased JID localpart.
 * Without this, the per-chat dialog showed mangled localparts like
 * "Wr682gv Ivan Be 32144" instead of "Ivan Benyk".
 */
export function useDriverDisplayName(
  driverJid: string,
  chatJid?: string,
): string {
  const chats = useAppSelector((s) => s.chats)
  const profiles = useAppSelector((s) => s.profiles)
  const accountChats = useAppSelector((s) => s.accountChats)
  // resolveDriverDisplayName's fallback when it knows nothing, in the current
  // UI language.
  const { t } = useTranslation()
  const unknown = t("reports.shared.unknown")

  return useMemo(() => {
    const safeProfiles = profiles ?? []
    const resolved = resolveDriverDisplayName({
      chatJid,
      driverJid,
      chats: chats ?? [],
      profiles: safeProfiles,
      accountChats: accountChats ?? {},
    })
    if (resolved && resolved !== driverJid && resolved !== unknown) {
      return resolved
    }
    const profile = safeProfiles.find((p) => p.jid === driverJid)
    return (
      (profile?.firstName && profile?.lastName
        ? `${profile.firstName} ${profile.lastName}`
        : undefined) ??
      formatJidLocalpartAsName(driverJid) ??
      driverJid
    )
  }, [driverJid, chatJid, chats, profiles, accountChats, unknown])
}
